import { Component, OnInit, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CategoryService } from 'src/app/services/category.service';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';
import { DiscountCouponsService } from 'src/app/services/discount-coupons.service';
import { AcademyService } from '../../service/academies.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Calendar } from 'primeng/calendar';

interface Week {
  label: string,
  value: string
}

interface Week {
  label: string,
  value: string
}

@Component({
  selector: 'app-add-coupon',
  templateUrl: './add-coupon.component.html',
  styleUrls: ['./add-coupon.component.scss'],
  providers: [ConfirmationService, MessageService]
})

export class AddCouponComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  discountCouponsForm: FormGroup;
  errorMessage = '';
  submitted = false;
  pagetitle: string;
  couponDiscountTypes: any = [];
  couponsModules: any = [];
  pushNotificationAppTypes: any = [];
  notificationIconPreview: string | null = null;
  notificationIconFile: File | null = null;
  couponsUsage: any;
  weeks: Week[] = environment.weeks;
  discountType: any;
  yearRange = environment.yearRange;
  academyId: string;
  academiesList: any;
  couponsRefundHours: any[] = [];
  disableCouponHours: boolean[] = [];
  isCouponHourSelected: boolean = false;
  today: Date = new Date();
  minEndDate: Date = new Date();
  availabilityErrors: { fromError: boolean, toError: boolean, timeError: boolean }[] = [];
  

  constructor(private discountCouponService: DiscountCouponsService, private formBuilder: FormBuilder, private messageService: MessageService,
    private router: Router, private categoryService: CategoryService, private activatedRoute: ActivatedRoute, private academyService: AcademyService) { }

  async ngOnInit(): Promise<void> {
    this.pagetitle = 'Add Coupon';
    this.academyId = this.activatedRoute.snapshot.paramMap.get('academyId');
    this.couponDiscountTypes = [
      { label: 'Percentage (%)', value: 'percentage' },
      { label: 'Flat Amount (₹)', value: 'flat' }
    ];

    this.discountCouponsForm = this.formBuilder.group({
      module: [null, [Validators.required]],
      name: [null, [Validators.required]],
      type: [null, [Validators.required]],
      value: [null, [Validators.required]],
      description: [null, [Validators.required]],
      code: [null, [Validators.required]],
      academyId: [null],
      termsAndCondition: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      usageId: [null, [Validators.required]],
      noOfTimes: [{ value: null, disabled: true }],
      availability: this.formBuilder.array([]),
      refund: this.formBuilder.array([
        this.refundFormGroup
      ])
    });

    this.weeks.forEach(week => {
      const availabilityFormGroup = this.formBuilder.group({
        active: [false],
        day: [week.label.slice(0, 3)],
        from: [null],
        to: [{ value: null, disabled: true }]
      })
      this.availabilityFormArray.push(availabilityFormGroup);
    })

    this.availabilityErrors = this.weeks.map(() => ({ fromError: false, toError: false, timeError: false }));

    this.getCouponModules();
    this.getCouponRefundHours();
    this.getCouponUsageDetails();
    if (!this.academyId) {
      this.getAcademyList();
      this.discountCouponsForm.get('academyId').setValidators([Validators.required]);
      this.discountCouponsForm.get('academyId').updateValueAndValidity();
    } else {
      this.discountCouponsForm.get('academyId').setValue(this.academyId);
    }
  }

  getAcademyList() {
    let query = "?filterByDisable=false";
    this.academyService.getAcademyList(query).pipe(takeUntil(this.destroy$)).subscribe(
      (data) => {
        this.academiesList = data.data.items;
      });
  }

  get availabilityFormArray(): FormArray {
    return this.discountCouponsForm.get('availability') as FormArray;
  }

  get refundFormArray(): FormArray {
    return this.discountCouponsForm.get('refund') as FormArray;
  }

  get refundFormGroup(): FormGroup {
    return this.formBuilder.group({
      hours: [null, [Validators.required]],
      percentage: [null, [Validators.required, Validators.min(1), Validators.max(100)]]
    })
  }

  addRefund() {
    let couponHourSelected = this.refundFormArray.controls[this.refundFormArray.length - 1]['controls']['hours'].value;
    if (couponHourSelected) {
      let couponsRefundHour = this.couponsRefundHours.find(ele => ele.id === couponHourSelected.id);
      couponsRefundHour['inactive'] = true;
      this.disableCouponHours[this.refundFormArray.length - 1] = true;
    }
    this.refundFormArray.push(this.refundFormGroup);
    this.disableCouponHours.push(false);
    this.isCouponHourSelected = false;
  }

  removeRefund(i: number) {
    let couponHourSelected = this.refundFormArray.controls[i]['controls']['hours'].value;
    if (!couponHourSelected) {
      couponHourSelected = this.refundFormArray.controls[i - 1]['controls']['hours'].value;
    }
    let couponsRefundHour = this.couponsRefundHours.find(ele => ele.id === couponHourSelected.id);
    couponsRefundHour['inactive'] = false;
    this.refundFormArray.removeAt(i);
    this.disableCouponHours.splice(i, 1);
    this.disableCouponHours[this.refundFormArray.length - 1] = false;
    this.isCouponHourSelected = this.refundFormArray.value.every(ele => ele.hours !== null);
  }

  onSelectCouponHour(event) {
    const selectedCouponHourId = event.value.id;
    this.isCouponHourSelected = (!selectedCouponHourId) ? false : true;
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.discountCouponsForm.controls;
  }

  h(controlName: string, i: number) {
    return this.discountCouponsForm.controls[controlName]['controls'][i]['controls'];
  }

  getCouponModules() {
    const query = `?filterByParentCategory=couponmodule`;
    this.categoryService.getCategoryList(query).pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.couponsModules = data?.data?.items;
      if (this.academyId !== null) {
        this.discountCouponsForm.get('module').setValue(this.couponsModules.find(ele => ele.name === 'Book a court'));
        this.discountCouponsForm.get('module').disable();
      }
    })
  }

  getCouponRefundHours() {
    const query = `?filterByParentCategory=couponRefundHours`;
    this.categoryService.getCategoryList(query).pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.couponsRefundHours = data?.data?.items;
      this.couponsRefundHours.forEach(ele => {
        ele['inactive'] = false;
      })
    })
  }

  getCouponUsageDetails() {
    const query = `?filterByParentCategory=couponusage`;
    this.categoryService.getCategoryList(query).pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      this.couponsUsage = data?.data?.items;
    })
  }

  onSelectTime(event: any, time: string, index: number) {
    const currentTime = new Date(event);
    currentTime.setMinutes(0, 0, 0);
    this.discountCouponsForm.get('availability')['controls'][index]['controls'][time].setValue(currentTime);
    this.discountCouponsForm.get('availability')['controls'][index]['controls']['to'].setValidators([Validators.required]);
    this.discountCouponsForm.get('availability')['controls'][index]['controls']['to'].updateValueAndValidity();
    this.discountCouponsForm.get('availability')['controls'][index]['controls']['to'].enable();
    this.setTimeValidations(time, index);
    const start = this.discountCouponsForm.get('availability')['controls'][index]['controls']['from'].value;
    const end = this.discountCouponsForm.get('availability')['controls'][index]['controls']['to'].value;
    if (start && end) {
        this.availabilityErrors[index].timeError = end.getTime() <= start.getTime();
    } else {
        this.availabilityErrors[index].timeError = false;
    }
    this.availabilityErrors[index].fromError = false;
    this.availabilityErrors[index].toError = false;
  }


  setTimeValidations(dayTime, index) {
    const startTime = this.discountCouponsForm.get('availability')['controls'][index]['controls']['from'].value;
    const endTime = this.discountCouponsForm.get('availability')['controls'][index]['controls']['to'].value;
    if (startTime && endTime) {
      if (dayTime === 'startTime') {
        this.discountCouponsForm.get('availability')['controls'][index]['controls'][dayTime].setErrors((startTime.getHours() >= endTime.getHours()) ? { startTimeValidator: true } : null);
      } else {
        this.discountCouponsForm.get('availability')['controls'][index]['controls'][dayTime].setErrors((endTime.getHours() <= startTime.getHours()) ? { endTimeValidator: true } : null);
      }
    }
  }


  onSelectDiscountType(event) {
    this.discountType = event.value.value;
    this.discountCouponsForm.get('value').setValue(null);
    this.discountCouponsForm.get('value').setValidators((event.value.value === 'percentage') ? [Validators.required, Validators.min(1), Validators.max(100)] : [Validators.required, Validators.min(1)]);
    this.discountCouponsForm.get('value').updateValueAndValidity();
  }

  onSelectCouponUsage(event) {
    if (event.value.name === 'limited') {
      this.discountCouponsForm.get('noOfTimes').enable();
      this.discountCouponsForm.get('noOfTimes').setValidators([Validators.required]);
      this.discountCouponsForm.get('noOfTimes').updateValueAndValidity();
    } else {
      this.discountCouponsForm.get('noOfTimes').reset();
      this.discountCouponsForm.get('noOfTimes').disable();
    }
  }

  onSubmit() {
    this.submitted = true;
    const hasAvailabilityError = this.availabilityFormArray.controls.some((control, i) => {
    const active = control.get('active').value;
    if (active) {
        const from = control.get('from').value;
        const to = control.get('to').value;
        if (!from) { this.availabilityErrors[i].fromError = true; return true; }
        if (!to) { this.availabilityErrors[i].toError = true; return true; }
        if (to.getTime() <= from.getTime()) { this.availabilityErrors[i].timeError = true; return true; }
    }
    this.availabilityErrors[i] = { fromError: false, toError: false, timeError: false };
    return false;
    });
    if (hasAvailabilityError) return;
    if (this.discountCouponsForm.invalid) {
      return;
    }
    this.discountCouponsForm.enable();

    var discountCouponsFormData = JSON.parse(JSON.stringify(this.discountCouponsForm.value));
    discountCouponsFormData['module'] = discountCouponsFormData['module']['id'];
    discountCouponsFormData['type'] = discountCouponsFormData['type']['value'];
    discountCouponsFormData['startDate'] = moment(discountCouponsFormData['startDate']).format('yyyy-MM-DD');
    discountCouponsFormData['endDate'] = moment(discountCouponsFormData['endDate']).format('yyyy-MM-DD');
    discountCouponsFormData['usageId'] = discountCouponsFormData['usageId']['id'];
    discountCouponsFormData['availability'].forEach(ele => {
      if (ele['from'] !== null) {
        ele['from'] = moment(ele['from']).format('hh:mm A');
      }
      if (ele['to'] !== null) {
        ele['to'] = moment(ele['to']).format('hh:mm A');
      }
    });
    discountCouponsFormData['refund'].forEach(ele => {
      if (ele['hours']) {
        ele['hours'] = ele['hours']['id']
      }
    });

    discountCouponsFormData = JSON.stringify(discountCouponsFormData, null, 4);
    this.addDiscountCoupons(discountCouponsFormData);
  }

  addDiscountCoupons(discountCouponsFormData) {
    this.discountCouponService.addDiscountCoupon(discountCouponsFormData).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.messageService.add({ key: 'toastmsg', severity: 'success', summary: 'Successful', detail: 'Discount Coupon Added Successfully!!', life: 6000 });
        setTimeout(() => {
          if (this.academyId) {
            this.router.navigate(['/manage/academies/discount-coupons/' + this.academyId]);
          } else {
            this.router.navigate(['/manage/discount-coupons']);
          }
        }, 2000);
      },
      ((err) => {
        this.messageService.add({ key: 'toastmsg', severity: 'error', summary: err.error.message, detail: 'Add Discount Coupon failed', life: 6000 });
      })
    );
  }

  onReset() {
    this.submitted = false;
    this.discountCouponsForm.reset();
  }

  backLink() {
    if (this.academyId) {
      this.router.navigate(['/manage/academies/discount-coupons/' + this.academyId]);
    } else {
      this.router.navigate(['/manage/discount-coupons']);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectStartDate(event) {
    this.minEndDate = new Date(event);
    const endDate = this.discountCouponsForm.get('endDate').value;
    if (endDate && new Date(endDate) < new Date(event)) {
        this.discountCouponsForm.get('endDate').setValue(null);
    }
  }

}
